import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Phone, ShieldAlert, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Overline } from "@/components/blocks";
import { api, formatApiError } from "@/lib/api";
import { CONSULTATION_TYPES, tel, EMERGENCY_NOTICE } from "@/data/site";
import { useContent } from "@/context/ContentContext";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Appointment() {
  const CLINIC = useContent();
  const [form, setForm] = useState({
    patientName: "", phone: "", email: "", requestedDate: "",
    requestedTime: "", consultationType: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (form.patientName.trim().length < 2) e.patientName = "Please enter the patient's name.";
    if (!/^[+0-9\s\-()]{6,}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone number.";
    if (form.email && !emailRe.test(form.email.trim())) e.email = "Please enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) { toast.error("Please check the highlighted fields."); return; }
    setSubmitting(true);
    try {
      await api.post("/appointments", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full rounded-xl border border-soft-border bg-white p-4 font-sans text-sm text-charcoal outline-none transition-[border-color,box-shadow] placeholder:text-taupe/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/30";
  const labelCls = "mb-2 block font-sans text-sm font-medium text-charcoal";

  return (
    <div>
      <SEO title="Book an Appointment | Dr. Mithee's Clinic for Women, Noida" path="/appointment" description="Request an appointment with Dr. Mithee Bhanot, Senior Consultant Obstetrician & Gynaecologist in Sector-110, Noida." />

      <section className="bg-gradient-to-b from-blush to-background pt-28 md:pt-36">
        <div className="container-clinic pb-12 text-center">
          <Reveal>
            <Overline>Appointment Request</Overline>
            <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-charcoal sm:text-5xl">Request an appointment</h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-taupe md:text-lg">
              Share a few details and our team will confirm your appointment by phone. This is a request form — not live scheduling.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-clinic pb-20 md:pb-28">
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            {done ? (
              <div data-testid="appointment-success" className="rounded-3xl border border-gold/30 bg-cream p-10 text-center shadow-card">
                <CheckCircle2 className="mx-auto h-14 w-14 text-terracotta" />
                <h3 className="mt-6 font-serif text-2xl text-charcoal md:text-3xl">Request received</h3>
                <p className="mx-auto mt-4 max-w-md text-taupe leading-relaxed">
                  Thank you for contacting Dr. Mithee's Clinic for Women. Your appointment request has been received. Our clinic team will contact you shortly to confirm.
                </p>
                <button data-testid="appointment-new-btn" onClick={() => { setDone(false); setForm({ patientName: "", phone: "", email: "", requestedDate: "", requestedTime: "", consultationType: "", message: "" }); }} className="mt-8 rounded-full border border-terracotta px-6 py-3 font-sans text-sm font-semibold text-terracotta transition-colors hover:bg-blush">
                  Make another request
                </button>
              </div>
            ) : (
              <form data-testid="appointment-form" onSubmit={submit} noValidate className="rounded-3xl border border-soft-border bg-cream p-6 shadow-card md:p-8">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="pname" className={labelCls}>Patient Name *</label>
                    <input id="pname" data-testid="appt-name" value={form.patientName} onChange={(e) => set("patientName", e.target.value)} className={inputCls} placeholder="Full name" />
                    {errors.patientName && <p className="mt-1.5 text-xs text-destructive">{errors.patientName}</p>}
                  </div>
                  <div>
                    <label htmlFor="pphone" className={labelCls}>Phone *</label>
                    <input id="pphone" type="tel" data-testid="appt-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+91 XXXXX XXXXX" />
                    {errors.phone && <p className="mt-1.5 text-xs text-destructive">{errors.phone}</p>}
                  </div>
                  <div>
                    <label htmlFor="pemail" className={labelCls}>Email</label>
                    <input id="pemail" type="email" data-testid="appt-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="Optional" />
                    {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="ptype" className={labelCls}>Consultation Type</label>
                    <select id="ptype" data-testid="appt-type" value={form.consultationType} onChange={(e) => set("consultationType", e.target.value)} className={inputCls}>
                      <option value="">Select type (optional)</option>
                      {CONSULTATION_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="pdate" className={labelCls}>Preferred Date</label>
                    <input id="pdate" type="date" data-testid="appt-date" value={form.requestedDate} onChange={(e) => set("requestedDate", e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="ptime" className={labelCls}>Preferred Time</label>
                    <input id="ptime" type="time" data-testid="appt-time" value={form.requestedTime} onChange={(e) => set("requestedTime", e.target.value)} className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="pmsg" className={labelCls}>Message</label>
                    <textarea id="pmsg" rows={4} data-testid="appt-message" value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} placeholder="Anything you'd like us to know (optional)" />
                  </div>
                </div>

                <div className="mt-5 flex items-start gap-3 rounded-xl bg-blush p-4">
                  <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
                  <p className="font-sans text-xs leading-relaxed text-taupe">{EMERGENCY_NOTICE}</p>
                </div>

                <button type="submit" data-testid="appt-submit" disabled={submitting} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-8 py-4 font-sans text-sm font-semibold text-cream shadow-soft transition-[transform,background-color] duration-300 hover:bg-terracotta-dark active:scale-95 disabled:opacity-70 sm:w-auto">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? "Submitting…" : "Request Appointment"}
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={120} className="lg:col-span-2">
            <div className="space-y-6">
              <div className="rounded-3xl bg-terracotta p-8 text-cream">
                <h2 className="font-serif text-2xl">Prefer to call?</h2>
                <p className="mt-3 text-sm leading-relaxed text-cream/80">Speak directly with our clinic team for the quickest booking.</p>
                <a href={tel(CLINIC.appointmentMobile)} data-testid="appt-call-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3.5 font-sans text-sm font-semibold text-terracotta">
                  <Phone className="h-4 w-4" /> {CLINIC.appointmentMobile}
                </a>
              </div>
              <div className="rounded-3xl border border-soft-border bg-cream p-8">
                <h2 className="flex items-center gap-2 font-serif text-2xl text-charcoal"><Clock className="h-5 w-5 text-terracotta" /> Timings</h2>
                <ul className="mt-6 space-y-3 text-sm">
                  {CLINIC.timingsList.map((t) => (
                    <li key={t.day + t.time} className="flex items-center justify-between gap-4 border-b border-soft-border pb-3 last:border-none last:pb-0">
                      <span className="font-medium text-charcoal">{t.day}</span>
                      <span className="whitespace-nowrap text-right font-medium text-terracotta">{t.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
